import React, { ReactNode } from "react";
import { useRenderOn } from "src/hooks";

import { IBackground, IBanner, ITemplateType } from "../../types";

import RenderBackground from "./RenderBackground";
import RenderHtml from "./RenderHtml";
import RenderImage from "./RenderImage";

interface IProps {
  children?: ReactNode;
  templateType: ITemplateType;
  banner: IBanner;
  pageType?: string;
  brandId?: string;
}

/**
 * Content of banners
 * @param templateType
 * @param banner
 * @param pageType
 * @param brandId
 */
const RenderContent = ({
  children,
  templateType,
  banner,
  pageType,
  brandId,
}: IProps): JSX.Element | null => {
  const { xs, sm, mdOrUp } = useRenderOn();
  const { banner_image, banner_image_large } = banner;

  /**
   * Preheader content
   */
  if (templateType === "preheader" && banner.html_content) {
    // TODO: Hide and animate more one banner (CSS)

    return <RenderHtml className="preheader" content={banner.html_content} />;
  }

  /**
   * Undermenu content
   */
  if (templateType === "underMenu") {
    const pageTypeTarget = banner.page_type_target
      ? banner.page_type_target.toLowerCase()
      : undefined;

    const isPageTypeAllowed = (): boolean => {
      if (pageTypeTarget === "all") {
        return true;
      } else if (pageType === "checkout" && pageTypeTarget === "checkout") {
        return true;
      } else if (pageTypeTarget === "all without checkout steps") {
        return pageType === "checkout" || pageType === "funnel" ? false : true;
      } else if (pageTypeTarget === "brand") {
        return pageType === "category" || pageType === "product" ? true : false;
      }

      return false;
    };

    // Targeted page matches with the current page
    if (isPageTypeAllowed()) {
      const brandIdTarget = banner.brand_id_target
        ? banner.brand_id_target.toString()
        : undefined;

      const isBrandIsAllowed =
        brandIdTarget === undefined || brandIdTarget === brandId;

      if (isBrandIsAllowed) {
        // Image content: (it overwrites the html content)
        if ((xs || sm) && banner_image?.url) {
          return (
            <RenderImage
              className="undermenu"
              src={banner_image.url}
              alt={banner_image?.alt}
            />
          );
        }

        if (mdOrUp && banner_image_large?.url) {
          return (
            <RenderImage
              className="undermenu"
              src={banner_image_large.url}
              alt={banner_image_large?.alt}
            />
          );
        }

        // or Html content: the banner should have at least HTML content
        else if (banner.html_content) {
          return (
            <RenderHtml
              className="banner.html_content"
              content={banner.html_content}
            />
          );
        }
      }

      // no content
      return null;
    }

    // no match with type of the page
    return null;
  }

  /**
   * Product page
   */
  if (templateType === "product") {
    if (banner_image || banner_image_large) {
      if ((xs || sm) && banner_image?.url) {
        return (
          <RenderImage
            className="product-banner"
            src={banner_image.url}
            alt={banner_image?.alt}
          />
        );
      }

      if (mdOrUp && banner_image_large?.url) {
        return (
          <RenderImage
            className="product-banner"
            src={banner_image_large.url}
            alt={banner_image_large?.alt}
          />
        );
      }
    }

    return null;
  }

  /**
   * Background
   */
  if (templateType === "background") {
    const {
      banner_image: image,
      background_color: backgroundColor,
      background_header_height: headerHeight,
      background_is_repeated: repeat,
      background_is_fixed: fixed,
      banner_redirect: redirect,
    } = banner as IBackground;

    if (image) {
      if (image?.url && redirect) {
        return (
          <RenderBackground
            src={image.url}
            headerHeight={headerHeight}
            backgroundColor={backgroundColor}
            repeat={repeat}
            fixed={fixed}
            to={redirect}
          >
            {children}
          </RenderBackground>
        );
      }

      return children ? <>{children}</> : null;
    }

    return children ? <>{children}</> : null;
  }

  /**
   * Other banners : Homepage, Order confirmation
   */
  const classNameDefault =
    templateType === "orderConfirmation" ? "border-op" : undefined;

  if (banner_image?.url) {
    return (
      <RenderImage
        className={classNameDefault}
        src={banner_image.url}
        alt={banner_image?.alt}
      />
    );
  }

  return null;
};

export default RenderContent;
